import { useState, useEffect } from 'react';
import { getAnalyticsSummary, getAnalyticsDaily, getAnalyticsEvents, getAdminUsers } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Users, ShoppingBag, Eye, MousePointerClick } from 'lucide-react';

export default function AdminAnalytics() {
  const [summary, setSummary] = useState(null);
  const [daily, setDaily] = useState([]);
  const [events, setEvents] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eventFilter, setEventFilter] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const [s, d, e, u] = await Promise.all([
          getAnalyticsSummary(), getAnalyticsDaily(30), getAnalyticsEvents({ limit: 100 }), getAdminUsers()
        ]);
        setSummary(s.data);
        setDaily(d.data);
        setEvents(e.data);
        setUsers(u.data);
      } catch {}
      setLoading(false);
    };
    load();
  }, []);

  const loadFilteredEvents = async (type) => {
    setEventFilter(type);
    try {
      const res = await getAnalyticsEvents({ limit: 100, event_type: type || undefined });
      setEvents(res.data);
    } catch {}
  };

  if (loading) {
    return <div className="flex justify-center py-20"><div className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--accent)', borderTopColor: 'transparent' }} /></div>;
  }

  const eventTypes = ['page_view', 'product_click', 'buy_now_click', 'purchase', 'promo_code_copy', 'redirect_to_main_site', 'registration', 'abandoned_cart'];

  return (
    <div className="space-y-6" data-testid="admin-analytics">
      <div>
        <h1 className="font-heading text-2xl tracking-tight" style={{ fontFamily: 'var(--font-heading)', fontWeight: 600 }}>Analytics</h1>
        <p className="font-body text-sm" style={{ color: 'var(--text-muted)' }}>Dati di visualizzazione, acquisti e utenti</p>
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="mb-4" data-testid="analytics-tabs">
          <TabsTrigger value="overview" className="text-sm">Panoramica</TabsTrigger>
          <TabsTrigger value="events" className="text-sm">Eventi</TabsTrigger>
          <TabsTrigger value="users" className="text-sm">Utenti</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          {/* Daily Chart */}
          <Card className="border rounded-sm" style={{ borderColor: 'var(--border-light)' }} data-testid="daily-chart-card">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-lg" style={{ fontFamily: 'var(--font-heading)', fontWeight: 600 }}>Attivita Giornaliera (30gg)</CardTitle>
            </CardHeader>
            <CardContent>
              {daily.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={daily}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border-light)" />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fontFamily: 'var(--font-mono)' }} tickFormatter={v => v.slice(5)} />
                    <YAxis tick={{ fontSize: 10, fontFamily: 'var(--font-mono)' }} />
                    <Tooltip contentStyle={{ fontFamily: 'var(--font-body)', fontSize: 12, borderRadius: 2, border: '1px solid var(--border-light)' }} />
                    <Legend />
                    <Line type="monotone" dataKey="page_view" name="Visite" stroke="#2C4035" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="product_click" name="Click" stroke="#3B5243" strokeWidth={1.5} dot={false} />
                    <Line type="monotone" dataKey="purchase" name="Acquisti" stroke="#8A8D8A" strokeWidth={1.5} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-center py-12 font-body text-sm" style={{ color: 'var(--text-muted)' }}>Nessun dato disponibile</p>
              )}
            </CardContent>
          </Card>

          {/* Event Distribution */}
          <Card className="border rounded-sm" style={{ borderColor: 'var(--border-light)' }} data-testid="event-distribution-card">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-lg" style={{ fontFamily: 'var(--font-heading)', fontWeight: 600 }}>Distribuzione Eventi (mese)</CardTitle>
            </CardHeader>
            <CardContent>
              {summary ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={[
                    { name: 'Visite', value: summary.page_views },
                    { name: 'Click', value: summary.product_clicks },
                    { name: 'Acquista', value: summary.buy_now_clicks },
                    { name: 'Acquisti', value: summary.purchases_this_month },
                    { name: 'Promo', value: summary.promo_copies },
                    { name: 'Redirect', value: summary.redirects },
                  ]}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border-light)" />
                    <XAxis dataKey="name" tick={{ fontSize: 10, fontFamily: 'var(--font-body)' }} />
                    <YAxis tick={{ fontSize: 10, fontFamily: 'var(--font-mono)' }} />
                    <Tooltip contentStyle={{ fontFamily: 'var(--font-body)', fontSize: 12, borderRadius: 2 }} />
                    <Bar dataKey="value" fill="var(--accent)" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : null}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="events" className="space-y-4">
          {/* Event Filter */}
          <div className="flex flex-wrap gap-2" data-testid="event-filters">
            <Badge variant={!eventFilter ? 'default' : 'outline'} className="cursor-pointer text-xs rounded-sm"
              onClick={() => loadFilteredEvents('')}
              style={!eventFilter ? { backgroundColor: 'var(--accent)', color: '#fff' } : {}}>
              Tutti
            </Badge>
            {eventTypes.map(t => (
              <Badge key={t} variant={eventFilter === t ? 'default' : 'outline'} className="cursor-pointer text-xs rounded-sm"
                onClick={() => loadFilteredEvents(t)}
                style={eventFilter === t ? { backgroundColor: 'var(--accent)', color: '#fff' } : {}}>
                {t}
              </Badge>
            ))}
          </div>

          {/* Events Table */}
          <Card className="border rounded-sm" style={{ borderColor: 'var(--border-light)' }} data-testid="events-table-card">
            <CardContent className="p-0">
              <div className="overflow-auto">
                <table className="w-full text-sm" style={{ fontFamily: 'var(--font-body)' }}>
                  <thead>
                    <tr className="border-b" style={{ borderColor: 'var(--border-light)', backgroundColor: 'var(--bg-primary)' }}>
                      <th className="text-left p-3 font-medium text-xs" style={{ color: 'var(--text-secondary)' }}>Tipo</th>
                      <th className="text-left p-3 font-medium text-xs" style={{ color: 'var(--text-secondary)' }}>Utente</th>
                      <th className="text-left p-3 font-medium text-xs" style={{ color: 'var(--text-secondary)' }}>Campagna</th>
                      <th className="text-left p-3 font-medium text-xs" style={{ color: 'var(--text-secondary)' }}>Data</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.map((e, i) => (
                      <tr key={i} className="border-b last:border-b-0" style={{ borderColor: 'var(--border-light)' }}>
                        <td className="p-3"><Badge variant="outline" className="text-xs rounded-sm font-mono" style={{ fontFamily: 'var(--font-mono)' }}>{e.event_type}</Badge></td>
                        <td className="p-3 text-xs" style={{ color: 'var(--text-muted)' }}>{e.user_id || 'Anonimo'}</td>
                        <td className="p-3 text-xs" style={{ color: 'var(--text-muted)' }}>{e.campaign_id || '-'}</td>
                        <td className="p-3 text-xs font-mono" style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                          {e.created_at ? new Date(e.created_at).toLocaleString('it-IT') : ''}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {events.length === 0 && (
                  <p className="text-center py-8 font-body text-sm" style={{ color: 'var(--text-muted)' }}>Nessun evento trovato</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="users" className="space-y-4">
          <Card className="border rounded-sm" style={{ borderColor: 'var(--border-light)' }} data-testid="users-table-card">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-lg" style={{ fontFamily: 'var(--font-heading)', fontWeight: 600 }}>
                Utenti Registrati ({users.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-auto">
                <table className="w-full text-sm" style={{ fontFamily: 'var(--font-body)' }}>
                  <thead>
                    <tr className="border-b" style={{ borderColor: 'var(--border-light)', backgroundColor: 'var(--bg-primary)' }}>
                      <th className="text-left p-3 font-medium text-xs" style={{ color: 'var(--text-secondary)' }}>Nome</th>
                      <th className="text-left p-3 font-medium text-xs" style={{ color: 'var(--text-secondary)' }}>Email</th>
                      <th className="text-left p-3 font-medium text-xs" style={{ color: 'var(--text-secondary)' }}>Provider</th>
                      <th className="text-left p-3 font-medium text-xs" style={{ color: 'var(--text-secondary)' }}>Ruolo</th>
                      <th className="text-left p-3 font-medium text-xs" style={{ color: 'var(--text-secondary)' }}>Registrato</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u, i) => (
                      <tr key={i} className="border-b last:border-b-0" style={{ borderColor: 'var(--border-light)' }}>
                        <td className="p-3 text-sm flex items-center gap-2">
                          {u.picture && <img src={u.picture} alt="" className="w-6 h-6 rounded-full" />}
                          {u.name}
                        </td>
                        <td className="p-3 text-xs" style={{ color: 'var(--text-muted)' }}>{u.email}</td>
                        <td className="p-3"><Badge variant="outline" className="text-xs rounded-sm">{u.auth_provider}</Badge></td>
                        <td className="p-3">
                          <Badge className="text-xs rounded-sm" style={{
                            backgroundColor: u.role === 'admin' ? 'rgba(44,64,53,0.1)' : 'rgba(138,141,138,0.1)',
                            color: u.role === 'admin' ? 'var(--accent)' : 'var(--text-muted)'
                          }}>{u.role}</Badge>
                        </td>
                        <td className="p-3 text-xs font-mono" style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                          {u.created_at ? new Date(u.created_at).toLocaleDateString('it-IT') : ''}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {users.length === 0 && (
                  <p className="text-center py-8 font-body text-sm" style={{ color: 'var(--text-muted)' }}>Nessun utente registrato</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
